'use client'

/**
 * useVideoDownloadEntitlement — "may this visitor request watermarked video
 * downloads, and how much of their allowance is left?"
 *
 * Backed by GET /api/video-downloads/me, which answers { enabled, usage, caps }
 * and returns `enabled: false` (200, not 401) for an anonymous visitor so the
 * player download icon can hide itself without treating anonymity as an
 * error.
 *
 * Why a module-level cache: the icon mounts on EVERY Vimeo player. A scene
 * with a dozen clips, or a tutorial whose body embeds several videos, would
 * otherwise fire a dozen identical requests on load. The first mount kicks
 * off one fetch; every later mount (on this page load) shares the same
 * promise. `refresh()` re-fetches and pushes the new payload to every mounted
 * subscriber, so requesting a download from one player updates the usage
 * shown by the account Downloads tab (and vice versa) without a reload.
 *
 * SSR-safe: the initial state is always `loading` and the fetch only starts
 * from an effect, so server HTML and the first client render agree (no
 * hydration mismatch, and nothing renders for members until the answer is
 * in — the icon appears a tick later rather than flashing for everyone).
 *
 * A failed fetch (network blip, 5xx) is NOT cached: it resolves the current
 * subscribers to "disabled" and clears the slot so the next mount retries.
 */

import { useCallback, useEffect, useState } from 'react'

export interface VideoDownloadUsage {
  today: number
  month: number
}

export interface VideoDownloadCaps {
  /** 0 = uncapped. */
  daily: number
  /** 0 = uncapped. */
  monthly: number
  retentionDays: number
}

export interface VideoDownloadEntitlement {
  enabled: boolean
  /** How the caller qualified — `admin` (role bypass) or `plan` (entitled
   *  subscription). Null when not entitled or unauthenticated. Lets the UI
   *  label an admin's access without implying they hold Legacy Premium. */
  via: 'admin' | 'plan' | null
  usage: VideoDownloadUsage
  /** Null while unauthenticated (the route omits caps for anonymous callers). */
  caps: VideoDownloadCaps | null
}

const DISABLED: VideoDownloadEntitlement = {
  enabled: false,
  via: null,
  usage: { today: 0, month: 0 },
  caps: null,
}

let inflight: Promise<VideoDownloadEntitlement> | null = null
const subscribers = new Set<(value: VideoDownloadEntitlement) => void>()

function toNumber(v: unknown, fallback = 0): number {
  const n = typeof v === 'number' ? v : Number.parseInt(String(v ?? ''), 10)
  return Number.isFinite(n) ? n : fallback
}

/** Coerce the route payload defensively — a malformed answer degrades to
 *  "disabled" rather than throwing into render. */
function normalize(raw: unknown): VideoDownloadEntitlement {
  if (!raw || typeof raw !== 'object') return DISABLED
  const r = raw as Record<string, unknown>
  const usage = (r.usage ?? {}) as Record<string, unknown>
  const caps = r.caps && typeof r.caps === 'object' ? (r.caps as Record<string, unknown>) : null
  return {
    enabled: r.enabled === true,
    via: r.via === 'admin' ? 'admin' : r.via === 'plan' ? 'plan' : null,
    usage: { today: toNumber(usage.today), month: toNumber(usage.month) },
    caps: caps
      ? {
          daily: toNumber(caps.daily, 10),
          monthly: toNumber(caps.monthly, 100),
          retentionDays: toNumber(caps.retentionDays, 7),
        }
      : null,
  }
}

function load(): Promise<VideoDownloadEntitlement> {
  if (inflight) return inflight
  const p = fetch('/api/video-downloads/me', { cache: 'no-store', credentials: 'same-origin' })
    .then(async (res) => (res.ok ? normalize(await res.json()) : DISABLED))
    .catch(() => {
      // Don't pin a transient failure for the whole page load.
      inflight = null
      return DISABLED
    })
    .then((value) => {
      for (const fn of subscribers) fn(value)
      return value
    })
  inflight = p
  return p
}

/** Drop the cached answer and fetch again, notifying every mounted hook. */
export function refreshVideoDownloadEntitlement(): Promise<VideoDownloadEntitlement> {
  inflight = null
  return load()
}

export interface UseVideoDownloadEntitlementResult extends VideoDownloadEntitlement {
  /** True until the first answer for this page load arrives. */
  loading: boolean
  /** Re-fetch (e.g. after a request changed the usage counters). */
  refresh: () => Promise<VideoDownloadEntitlement>
}

export function useVideoDownloadEntitlement(): UseVideoDownloadEntitlementResult {
  const [value, setValue] = useState<VideoDownloadEntitlement | null>(null)

  useEffect(() => {
    let alive = true
    const onValue = (v: VideoDownloadEntitlement) => {
      if (alive) setValue(v)
    }
    subscribers.add(onValue)
    // Shares the in-flight promise with every other mounted hook; a hook
    // mounting after the answer landed resolves immediately from it.
    void load().then(onValue)
    return () => {
      alive = false
      subscribers.delete(onValue)
    }
  }, [])

  const refresh = useCallback(() => refreshVideoDownloadEntitlement(), [])

  // Render from React state only (never from the module cache) so server
  // HTML and the hydrating client render are identical.
  const current = value ?? DISABLED
  return {
    loading: value === null,
    enabled: current.enabled,
    via: current.via,
    usage: current.usage,
    caps: current.caps,
    refresh,
  }
}
